// screen: Storage — bucket usage under the tenant prefix, growth, recording retention (Studio spec §4.12; control-plane
// change `distributions`: /v1/storage/usage) and the tenant's own S3 bucket + keys (v3 §10.6: /v1/storage/s3,
// app/TenantS3.tsx).
import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { RefreshCw } from 'lucide-react';
import type { ApiError } from '@viewstream/api-client';
import { fmtDateTime } from '@viewstream/i18n';
import { Badge, Button, ErrorState, Skeleton } from '@viewstream/ui';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';
import { can } from '~/app/permissions';
import { fmtBytes, useRefreshStorage, useStorageUsage, type StorageUsage } from '~/app/delivery';

type RetRow = StorageUsage['retention'][number] & { visibility?: string; bytes_per_day?: number; bytes_per_day_measured?: boolean; clean_feed_of?: string };
import { TenantS3Section } from '~/app/TenantS3';

export const Route = createFileRoute('/_layout/t/$slug/storage')({ component: Screen });

// Time always runs left → right, also in Hebrew (Dmitry 2026-10-05: the mirrored RTL line read as shrinking while it
// grew 74 → 126 GB); both ends are labelled with their date and size.
function Sparkline({ points, label, locale }: { points: StorageUsage['history']; label: string; locale: string }) {
  if (points.length < 2) return null;
  const w = 240, h = 48;
  const max = Math.max(...points.map((p) => p.bytes)) || 1;
  const min = Math.min(...points.map((p) => p.bytes));
  const span = max - min || 1;
  const d = points.map((p, i) => `${(i / (points.length - 1)) * w},${h - ((p.bytes - min) / span) * (h - 4) - 2}`).join(' ');
  const first = points[0]!, last = points[points.length - 1]!;
  return (
    <div dir="ltr" className="inline-block">
      <svg role="img" aria-label={label} width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="text-primary">
        <polyline points={d} fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
      <div className="flex justify-between gap-4 text-xs text-fg-muted tabular-nums" style={{ width: w }}>
        <span>{first.date} · {fmtBytes(first.bytes, locale)}</span>
        <span>{last.date} · {fmtBytes(last.bytes, locale)}</span>
      </div>
    </div>
  );
}

function Screen() {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const { locale, tz } = useLocale();
  const q = useStorageUsage(tenant.slug);
  const refresh = useRefreshStorage(tenant.slug);
  React.useEffect(() => { document.title = appTitle(t`Storage`); }, [t]);
  const bucketLabel: Record<string, string> = { vod: t`Library (VOD renditions)`, rec: t`Recordings`, masters: t`Masters (originals)`, ingest: t`Upload staging` };
  const at = (iso: string | null | undefined) => (iso ? fmtDateTime(iso, { locale, tz }, { dateStyle: 'medium', timeStyle: 'short' }) : '—');
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">{t`Storage`}</h1>
      <section aria-labelledby="usage-h" className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 id="usage-h" className="text-lg font-semibold">{t`Usage`}</h2>
          {can(scopes, 'storage:manage') ? (
            <Button variant="secondary" size="sm" disabled={refresh.isPending || q.data?.refreshing} onClick={() => refresh.mutate()}>
              <RefreshCw className={`size-4 ${q.data?.refreshing ? 'animate-spin' : ''}`} aria-hidden /> {q.data?.refreshing ? t`Counting…` : t`Refresh now`}
            </Button>
          ) : null}
        </div>
        {q.isError ? <ErrorState error={q.error as ApiError} onRetry={() => q.refetch()} labels={{ title: t`Could not load storage usage`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} /> : null}
        {q.isPending ? <Skeleton className="h-32" /> : null}
        {q.data ? (
          <>
            <p className="text-sm text-fg-muted">
              {q.data.taken_at ? t`Inventory of objects under ${q.data.prefix} taken ${at(q.data.taken_at)}.` : t`No count yet — storage is counted once a day, overnight.`}
              {q.data.taken_at ? <> {t`Counted once a day, overnight.`}</> : null}
              {q.data.refreshing ? <Badge className="ms-2">{t`counting`}</Badge> : null}
            </p>
            <table className="w-full max-w-2xl text-sm" data-testid="storage-table">
              <thead className="text-xs text-fg-muted">
                <tr><th className="text-start">{t`Bucket`}</th><th className="text-end">{t`Objects`}</th><th className="text-end">{t`Size`}</th></tr>
              </thead>
              <tbody>
                {q.data.buckets.map((b) => (
                  <tr key={b.bucket} className="border-t border-border">
                    <td className="py-2">{bucketLabel[b.bucket] ?? b.bucket} <code className="ltr-isolate text-xs text-fg-muted">{b.bucket}/{q.data.prefix}</code></td>
                    <td className="text-end tabular-nums">{new Intl.NumberFormat(locale).format(b.objects)}</td>
                    <td className="text-end tabular-nums" data-testid={`bytes-${b.bucket}`}>{fmtBytes(b.bytes, locale)}</td>
                  </tr>
                ))}
                <tr className="border-t border-border font-medium">
                  <td className="py-2">{t`Total`}</td><td /><td className="text-end tabular-nums">{fmtBytes(q.data.total_bytes, locale)}</td>
                </tr>
              </tbody>
            </table>
            {q.data.history.length > 1 ? (
              <div className="space-y-1">
                <h3 className="text-sm font-medium">{t`Growth (daily)`}</h3>
                <Sparkline points={q.data.history} locale={locale} label={t`Total size per day, ${q.data.history[0]?.date} to ${q.data.history[q.data.history.length - 1]?.date}`} />
              </div>
            ) : <p className="text-xs text-fg-muted">{t`A growth chart appears once there are counts from two or more days.`}</p>}
          </>
        ) : null}
      </section>

      {q.data && q.data.retention.length > 0 ? (
        <section aria-labelledby="ret-h" className="space-y-2">
          <h2 id="ret-h" className="text-lg font-semibold">{t`Retention`}</h2>
          <table className="w-full max-w-2xl text-sm">
            <thead className="text-xs text-fg-muted"><tr><th className="text-start">{t`Channel`}</th><th className="text-end">{t`Recordings kept`}</th><th className="text-end">{t`DVR window`}</th><th className="text-end">{t`Per day`}</th></tr></thead>
            {/* change c14-clean-feed F8: internal channels (clean feeds) listed separately, with their GB per day */}
            {(['public', 'internal'] as const).map((vis) => {
              const rows = (q.data!.retention as RetRow[]).filter((r) => (r.visibility ?? 'public') === vis);
              if (!rows.length) return null;
              return (
                <tbody key={vis} data-testid={`retention-${vis}`}>
                  {vis === 'internal' ? <tr><th colSpan={4} className="pt-3 text-start text-xs font-medium text-fg-muted">{t`Internal channels (not shown to viewers)`}</th></tr> : null}
                  {rows.map((r) => (
                    <tr key={r.channel} className="border-t border-border">
                      <td className="py-2">{r.title || r.channel}{r.clean_feed_of ? <span className="ms-2 text-xs text-fg-muted">{t`clean feed of ${r.clean_feed_of}`}</span> : null}</td>
                      <td className="text-end">{t`${r.retention_days} days`}</td>
                      <td className="text-end">{t`${Math.round(r.dvr_window_s / 60)} min`}</td>
                      <td className="text-end num">{r.bytes_per_day ? fmtBytes(r.bytes_per_day, locale) : '—'}{r.bytes_per_day_measured ? <span className="ms-1 text-xs text-fg-muted">{t`measured`}</span> : vis === 'internal' ? <span className="ms-1 text-xs text-fg-muted">{t`estimate`}</span> : null}</td>
                    </tr>
                  ))}
                </tbody>
              );
            })}
          </table>
          <p className="text-xs text-fg-muted">{t`Recordings older than the retention are deleted automatically; clips and published programmes are kept as library assets. Retention is set per channel (Live → channel → Recording).`}</p>
        </section>
      ) : null}

      <section aria-labelledby="keys-h" className="space-y-2">
        <h2 id="keys-h" className="text-lg font-semibold">{t`S3 bucket`}</h2>
        <TenantS3Section tenant={tenant.slug} scopes={scopes} />
      </section>
    </div>
  );
}
